import { ProductKey } from './ProductIcons';
import SettingsSection from './ProductNavSettings';
import * as Styled from './Products.styled';
import ProductsNavSection from './ProductsNavSection';
import { useHasGuardAccess } from '@modules/auth/hooks/useHasGuardAccess';
import { hasWorkspaceProductAccess, hasWorkspaceSystemInsightsAccess } from '@modules/auth/utils/AuthorizationService/AuthorizationService';
import { useFlagsProxy } from '@modules/feature-flags/hooks/useFlagsProxy';
import { useGetLeaderSystemInfo } from '@modules/leader/api/useGetLeaderSystemInfo';
import { useOrganization } from 'app/_providers/Organization';
import { useUser } from 'app/_providers/User';
import { useWorkspace } from 'app/_providers/WorkspaceProvider';

import { useSelectedLayoutSegments } from 'next/navigation';
import * as React from 'react';

interface IItem {
  title: string;
  link: string;
  perm?: Permissions;
  icon?: React.ReactNode;
  isPreview?: boolean;
  isActive?: boolean;
}

export interface ISectionData {
  productKey: ProductKey;
  items: IItem[];
  hasAccess?: boolean;
}

type ActiveGlobalItem = 'workspace' | 'organization';

const insightsItems = (
  leaderUrl: string,
): IItem[] => [
  { title: 'Insights', link: `${leaderUrl}/insights` },
  { title: 'System', link: `${leaderUrl}/insights/system` },
  { title: 'Data', link: `${leaderUrl}/insights/data` },
  { title: 'Alerts', link: `${leaderUrl}/insights/alerts` },
];

const streamItems = (leaderUrl: string, hasEnterpriseGuardAccess?: boolean): IItem[] => [
  { title: 'Stream', link: `${leaderUrl}/stream` },
  { title: 'Worker Groups', link: `${leaderUrl}/stream/m` },
  { title: 'Workers', link: `${leaderUrl}/stream/workers` },
  { title: 'Outposts', link: `${leaderUrl}/stream/outposts` },
  { title: 'Mappings', link: `${leaderUrl}/stream/mappings` },
  { title: 'Monitoring', link: `${leaderUrl}/stream/dashboard` },
  ...(hasEnterpriseGuardAccess ? [{ title: 'Guard', link: `${leaderUrl}/stream/guard` }] : []),
  { title: 'Notifications', link: `${leaderUrl}/stream/notifications` },
];

const edgeItems = (leaderUrl: string): IItem[] => [
  { title: 'Edge', link: `${leaderUrl}/edge` },
  { title: 'Fleets', link: `${leaderUrl}/edge/m` },
  { title: 'Edge Nodes', link: `${leaderUrl}/edge/nodes` },
  { title: 'Outposts', link: `${leaderUrl}/edge/outposts` },
  { title: 'Mappings', link: `${leaderUrl}/edge/mappings` },
  { title: 'Logs', link: `${leaderUrl}/edge/logs` },
  { title: 'Notifications', link: `${leaderUrl}/edge/notifications` },
];

const searchItems = (leaderUrl: string): IItem[] => {
  const defaultSearchItems = [
    { title: 'Search', link: `${leaderUrl}/search` },
    { title: 'History', link: `${leaderUrl}/search/history` },
    { title: 'Saved Searches', link: `${leaderUrl}/search/saved` },
    { title: 'Dashboards', link: `${leaderUrl}/search/dashboards` },
    { title: 'Notebooks', link: `${leaderUrl}/search/notebooks` },
    { title: 'Data', link: `${leaderUrl}/search/data` },
    { title: 'Knowledge', link: `${leaderUrl}/search/knowledge` },
    { title: 'Packs', link: `${leaderUrl}/search/p` },
  ];

  return defaultSearchItems;
};

const lakeItems = (leaderUrl: string): IItem[] => [
  { title: 'Lake', link: `${leaderUrl}/lake` },
  { title: 'Datasets', link: `${leaderUrl}/lake/datasets` },
  { title: 'Lakehouses', link: `${leaderUrl}/lake/lakehouses` },
  { title: 'Direct Access', link: `${leaderUrl}/lake/sources` },
  { title: 'Storage Locations', link: `${leaderUrl}/lake/storage-locations` },
  { title: 'Monitoring', link: `${leaderUrl}/lake/monitoring` },
];

const globalItems = (organizationId: string, workspaceId: string, activeItem?: ActiveGlobalItem): IItem[] => [
  {
    title: 'Cribl',
    link: `/organizations/${organizationId}/workspaces/${workspaceId}`,
  },
  {
    title: 'Workspace',
    link: `/organizations/${organizationId}/workspaces/${workspaceId}`,
    isActive: activeItem === 'workspace',
  },
  {
    title: 'Organization',
    link: `/organizations/${organizationId}/details`,
    isActive: activeItem === 'organization',
  },
];

const ProductsNav = () => {
  const userInfo = useUser();
  const { cloudControlPlaneCdnUi } = useFlagsProxy();
  const { name: workspaceName, leaderFQDN } = useWorkspace();
  const { organizationId, license } = useOrganization();
  const { data: systemInfo } = useGetLeaderSystemInfo({ leaderFQDN });
  const segments = useSelectedLayoutSegments();

  const licenseInfo = systemInfo?.items.find((item) => item.license.limits);
  const limits = licenseInfo?.license?.limits;
  const hasEnterpriseGuardAccess = useHasGuardAccess(limits, 'rbac', 1);
  const cloudSystemInsightsEnabled = systemInfo?.items[0]?.insightsEnabled ?? false;

  const getActiveGlobalItem = () => {
    if (segments.includes('workspaces')) {
      return 'workspace';
    }

    return 'organization';
  };

  const activeGlobalItem = getActiveGlobalItem();

  const hrefRoot = cloudControlPlaneCdnUi
    ? `https://app.${process.env.SHARED_DOMAIN}/organizations/${organizationId}/workspaces/${workspaceName}/app`
    : `https://${leaderFQDN}`;

  const sections: ISectionData[] = [
    {
      productKey: 'global',
      items: globalItems(organizationId, workspaceName, activeGlobalItem),
      hasAccess: true,
    },
    {
      productKey: 'insights' as ProductKey,
      items: insightsItems(hrefRoot),
      // TODO: https://taktak.atlassian.net/browse/MON-152
      hasAccess: hasWorkspaceSystemInsightsAccess(userInfo, cloudSystemInsightsEnabled, license),
    },
    {
      productKey: 'stream',
      items: streamItems(hrefRoot, hasEnterpriseGuardAccess),
      hasAccess: hasWorkspaceProductAccess(userInfo, workspaceName, 'stream'),
    },
    {
      productKey: 'edge',
      items: edgeItems(hrefRoot),
      hasAccess: hasWorkspaceProductAccess(userInfo, workspaceName, 'edge'),
    },
    {
      productKey: 'search',
      items: searchItems(hrefRoot),
      hasAccess: hasWorkspaceProductAccess(userInfo, workspaceName, 'search'),
    },
    {
      productKey: 'lake',
      items: lakeItems(hrefRoot),
      hasAccess: hasWorkspaceProductAccess(userInfo, workspaceName, 'lake'),
    },
  ];

  return (
    <Styled.Nav>
      {sections.map((section) => (
        <React.Fragment key={section.productKey}>
          {section.hasAccess && <ProductsNavSection {...section} />}
        </React.Fragment>
      ))}

      <SettingsSection />
    </Styled.Nav>
  );
};

export default ProductsNav;
